import api from './api';
import {
  EventEditResponse,
  Organizer,
  OrganizerEditResponse,
  OrganizerPortalEvent,
  OrganizerInsights,
  OrganizerPortalSubmissions,
  OrganizerPortalSummary,
  ProposeEventRequest,
  ProposeOrganizerEditRequest,
  OrganizerVisibility,
} from '@radevents/shared';

/**
 * Der Veranstalter-Bereich.
 *
 * Alle Schreibwege sind Vorschlaege — nichts geht direkt live. Das ist Absicht: dieselbe
 * Pruef-Queue wie bei jedem anderen Vorschlag, nur mit Kennzeichnung „vom Veranstalter".
 */
const organizerPortalService = {
  /** Womit bin ich verknuepft? Leere Liste = dieses Konto hat keinen Veranstalter-Bereich. */
  me: async (): Promise<OrganizerPortalSummary[]> => {
    const response = await api.get('/organizer-portal/me');
    return response.data;
  },

  profile: async (organizerId: number): Promise<Organizer> => {
    const response = await api.get(`/organizer-portal/${organizerId}/profile`);
    return response.data;
  },

  events: async (organizerId: number, includePast = false): Promise<OrganizerPortalEvent[]> => {
    const response = await api.get(`/organizer-portal/${organizerId}/events`, { params: { includePast } });
    return response.data;
  },

  submissions: async (organizerId: number): Promise<OrganizerPortalSubmissions> => {
    const response = await api.get(`/organizer-portal/${organizerId}/submissions`);
    return response.data;
  },

  /** Zahlen zu den eigenen Events — nur für verifizierte Veranstalter. */
  insights: async (organizerId: number, from?: string, to?: string): Promise<OrganizerInsights> => {
    const response = await api.get(`/organizer-portal/${organizerId}/insights`, { params: { from, to } });
    return response.data;
  },

  proposeNewEvent: async (organizerId: number, data: ProposeEventRequest): Promise<EventEditResponse> => {
    const response = await api.post(`/organizer-portal/${organizerId}/events/propose`, data);
    return response.data;
  },

  proposeEventEdit: async (
    organizerId: number,
    eventId: number,
    data: ProposeEventRequest,
  ): Promise<EventEditResponse> => {
    const response = await api.post(`/organizer-portal/${organizerId}/events/${eventId}/propose`, data);
    return response.data;
  },

  /**
   * Einen Entwurf veröffentlichen. Auch das ist ein Vorschlag: bei Auto-Freigabe sofort online,
   * sonst noch einmal durch die Prüfung — sonst wäre der Entwurf der Weg um sie herum.
   */
  publishEvent: async (organizerId: number, eventId: number): Promise<EventEditResponse> => {
    const response = await api.post(`/organizer-portal/${organizerId}/events/${eventId}/publish`);
    return response.data;
  },

  /**
   * Wer welche Stammdaten sieht — kein Vorschlag, sondern sofort wirksam.
   *
   * Ob die Telefonnummer des Kassenwarts im Netz steht, ist keine redaktionelle Frage. Sie durch
   * die Prüfliste zu schicken hieße, dass sie nach dem Entfernen noch tagelang dort steht.
   */
  updateVisibility: async (organizerId: number, visibility: OrganizerVisibility): Promise<Organizer> => {
    const response = await api.put(`/organizer-portal/${organizerId}/visibility`, visibility);
    return response.data;
  },

  /** Logo bzw. Banner als Datei — gehört zu Premium, der Server antwortet sonst mit 402. */
  uploadAsset: async (organizerId: number, kind: 'logo' | 'banner', file: File): Promise<Organizer> => {
    const form = new FormData();
    form.append('file', file);
    const response = await api.post(`/organizer-portal/${organizerId}/${kind}`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  deleteAsset: async (organizerId: number, kind: 'logo' | 'banner'): Promise<Organizer> => {
    const response = await api.delete(`/organizer-portal/${organizerId}/${kind}`);
    return response.data;
  },

  proposeProfileEdit: async (
    organizerId: number,
    data: ProposeOrganizerEditRequest,
  ): Promise<OrganizerEditResponse> => {
    const response = await api.post(`/organizer-portal/${organizerId}/propose`, data);
    return response.data;
  },
};

export default organizerPortalService;
